// ===================================================================
// components/ChallengeMode.tsx
// Mode Défi en ligne (« Arène », thème Làmb) : défie un ami via Supabase.
// Quiz culturel Afrique, avec thème, format (Éclair / Classique / Marathon)
// et mise d'XP facultative.
// Onglets : Créer · Reçus · Historique
// ===================================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Check, X } from 'lucide-react';
import BackButton from './common/BackButton';
import { useTheme } from './ThemeContext';
import './ChallengeArena.css';
import { MINI_GAMES_DATA } from '../data/miniGamesData';
import { quizQuestionText, quizOptionText } from '../data/miniGamesEn';
import { LanguageCode } from '../types/types';
import { getFollowing, getMyProfile, searchUsers, followUser, UserProfile, displayXP } from '../services/friendsService';
import {
  createChallenge,
  getPendingChallenges,
  completeChallenge,
  declineChallenge,
  getMatchHistory,
  getChallengeMeta,
  FORMAT_SPECS,
  Challenge,
  ChallengeQuestion,
  ChallengeTheme,
  ChallengeFormat,
  ChallengeMeta,
} from '../services/challengeService';

interface ChallengeModeProps {
  onBackToMenu: () => void;
  user: any | null;
  initialFriendId?: string | null;
  languageCode: LanguageCode;
  /** Ouvre le Memory Duel (accessible depuis l'onglet Créer) */
  onOpenMemoryDuel?: () => void;
}

type Tab        = 'create' | 'received' | 'history';
type CreateStep = 'pick_friend' | 'pick_mode' | 'playing' | 'saving' | 'sent';
type RecvStep   = 'list' | 'playing' | 'results';

const CHALLENGE_XP_PER_CORRECT = 10;
const CHALLENGE_WIN_BONUS      = 20;
const STAKE_STEP = 10;
const STAKE_MAX  = 100;
const URGENT_HOURS = 6;

const THEME_KEYS: Record<ChallengeTheme, string> = {
  Histoire:     'miniGames.themes.history',
  Géographie:   'miniGames.themes.geography',
  Politique:    'miniGames.themes.politics',
  Spiritualité: 'miniGames.themes.spirituality',
  mix:          'challenge.arena.mix',
};
const FORMAT_ORDER: ChallengeFormat[] = ['eclair', 'classique', 'marathon'];

// ─── Utilitaires ──────────────────────────────────────────────────
/**
 * Crédite l'XP sur la langue active (et non plus sur la langue la plus avancée)
 * et dispatche 'xpUpdated' : sans cet événement, l'XP de défi ne comptait ni
 * pour l'objectif du jour ni pour le streak, et n'était pas synchronisé.
 * `passive` : XP crédité sans activité (mise gagnée récupérée à l'ouverture de
 * l'historique) — compte dans l'objectif mais ne valide pas le streak.
 */
function addXP(lang: LanguageCode, amount: number, passive = false): void {
  if (amount <= 0) return;
  const key = `${lang}-totalXP`;
  const current = parseInt(localStorage.getItem(key) || '0', 10);
  const newTotal = current + amount;
  localStorage.setItem(key, String(newTotal));
  window.dispatchEvent(new CustomEvent('xpUpdated', {
    detail: { newTotal, gained: amount, source: 'challenge', passive },
  }));
}

function challengeXP(score: number, won: boolean, stake: number): number {
  return score * CHALLENGE_XP_PER_CORRECT + (won ? CHALLENGE_WIN_BONUS + stake : 0);
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function culturalPool(theme: ChallengeTheme): ChallengeQuestion[] {
  const all: ChallengeQuestion[] = [];
  Object.values(MINI_GAMES_DATA).forEach(countryData => {
    Object.entries(countryData.themes).forEach(([key, themeQs]: [string, any]) => {
      if (theme !== 'mix' && key !== theme) return;
      (themeQs ?? []).forEach((q: any) => {
        all.push({ word: q.question, translation: q.answer, choices: shuffle(q.options), correct: q.answer });
      });
    });
  });
  return all;
}

// Thèmes proposés : seulement ceux qui ont assez de questions pour un défi Éclair
const AVAILABLE_THEMES: ChallengeTheme[] = (['Histoire', 'Géographie', 'Politique', 'Spiritualité', 'mix'] as ChallengeTheme[])
  .filter(th => culturalPool(th).length >= FORMAT_SPECS.eclair.questions);

function initial(name?: string | null): string {
  return (name || '?').trim()[0]?.toUpperCase() || '?';
}

// Petites clés de stockage local (mises déjà créditées au challenger)
const STAKE_CLAIMED_KEY = 'challenge-stakes-claimed';
function readClaimed(): string[] {
  try { return JSON.parse(localStorage.getItem(STAKE_CLAIMED_KEY) || '[]'); } catch { return []; }
}

// ─── Composant ────────────────────────────────────────────────────
const ChallengeMode: React.FC<ChallengeModeProps> = ({ onBackToMenu, user, initialFriendId, languageCode, onOpenMemoryDuel }) => {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();

  const myName    = t('challenge.arena.you', 'Toi');
  const myInitial = initial(user?.name || user?.email);

  // ── TABS ──
  const [tab, setTab] = useState<Tab>('create');

  // ── CREATE FLOW ──
  const [createStep,    setCreateStep]    = useState<CreateStep>('pick_friend');
  const [friends,       setFriends]       = useState<UserProfile[]>([]);
  const [loadFriends,   setLoadFriends]   = useState(false);
  const [showAddFriend, setShowAddFriend] = useState(false);
  const [addQuery,      setAddQuery]      = useState('');
  const [addResults,    setAddResults]    = useState<UserProfile[]>([]);
  const [addLoading,    setAddLoading]    = useState(false);
  const [addMsg,        setAddMsg]        = useState<{ ok: boolean; text: string } | null>(null);
  const [chosenFriend,  setChosenFriend]  = useState<UserProfile | null>(null);
  const [chosenTheme,   setChosenTheme]   = useState<ChallengeTheme>('mix');
  const [chosenFormat,  setChosenFormat]  = useState<ChallengeFormat>('classique');
  const [stake,         setStake]         = useState(0);
  const [myQs,          setMyQs]          = useState<ChallengeQuestion[]>([]);
  const [myIdx,         setMyIdx]         = useState(0);
  const [myScore,       setMyScore]       = useState(0);
  const [mySelected,    setMySelected]    = useState<string | null>(null);
  const [myLocked,      setMyLocked]      = useState(false);
  const [myAnswers,     setMyAnswers]     = useState<(string | null)[]>([]);
  const [myTimes,       setMyTimes]       = useState<number[]>([]);
  const [saveErr,       setSaveErr]       = useState<string | null>(null);

  // ── RECEIVE FLOW ──
  const [recvStep,    setRecvStep]    = useState<RecvStep>('list');
  const [pendingList, setPendingList] = useState<Challenge[]>([]);
  const [sentPending, setSentPending] = useState<Challenge[]>([]);
  const [loadRecv,    setLoadRecv]    = useState(false);
  const [activeChall, setActiveChall] = useState<Challenge | null>(null);
  const [rvIdx,       setRvIdx]       = useState(0);
  const [rvScore,     setRvScore]     = useState(0);
  const [rvSelected,  setRvSelected]  = useState<string | null>(null);
  const [rvLocked,    setRvLocked]    = useState(false);
  const [rvAnswers,   setRvAnswers]   = useState<(string | null)[]>([]);
  const [rvTimes,     setRvTimes]     = useState<number[]>([]);

  // ── HISTORY ──
  const [histList, setHistList] = useState<Challenge[]>([]);
  const [loadHist, setLoadHist] = useState(false);

  // ── XP gagné en défi ──
  const [myXPEarned, setMyXPEarned] = useState(0);
  const [rvXPEarned, setRvXPEarned] = useState(0);

  // ── Message éphémère (partage / relance) ──
  const [toast, setToast] = useState<string | null>(null);

  // ── COUNTDOWN avant quiz ──
  const [countdown, setCountdown] = useState<number | null>(null); // 3→2→1→0("Partez!")→null

  // ── TIMER ──
  const mySeconds = FORMAT_SPECS[chosenFormat].seconds;
  const rvSeconds = activeChall ? FORMAT_SPECS[getChallengeMeta(activeChall.questions).format].seconds : FORMAT_SPECS.classique.seconds;
  const [timeLeft, setTimeLeft] = useState(mySeconds);
  const timerActive = countdown === null && (
    (createStep === 'playing' && !myLocked) ||
    (recvStep   === 'playing' && !rvLocked)
  );

  const doMyAdvance = useRef<((choice: string | null) => void) | null>(null);
  const doRvAdvance = useRef<((choice: string | null) => void) | null>(null);
  const qStart      = useRef<number>(Date.now());

  // ── LOAD FRIENDS ──
  useEffect(() => {
    if (!user?.id || tab !== 'create' || createStep !== 'pick_friend') return;
    setLoadFriends(true);
    getFollowing(user.id).then(setFriends).catch(() => setFriends([])).finally(() => setLoadFriends(false));
  }, [user, tab, createStep]);

  // ── RECHERCHE AMIS ──
  const handleAddSearch = useCallback(async (q: string) => {
    setAddQuery(q);
    setAddMsg(null);
    if (q.trim().length < 2) { setAddResults([]); return; }
    setAddLoading(true);
    try {
      const results = await searchUsers(q);
      setAddResults(results.filter(r => r.id !== user?.id && !friends.some(f => f.id === r.id)));
    } catch { setAddResults([]); }
    finally { setAddLoading(false); }
  }, [user, friends]);

  const handleFollowFromSearch = useCallback(async (profile: UserProfile) => {
    if (!user?.id) return;
    try {
      await followUser(user.id, profile.id);
      setFriends(prev => [...prev, profile]);
      setAddResults(prev => prev.filter(r => r.id !== profile.id));
      setAddMsg({ ok: true, text: t('challenge.addedFriend', { name: profile.display_name || profile.username }) });
    } catch {
      setAddMsg({ ok: false, text: t('challenge.addError') });
    }
  }, [user, t]);

  // ── PRÉ-SÉLECTION AMI (depuis profil) ──
  useEffect(() => {
    if (!initialFriendId) return;
    getMyProfile(initialFriendId)
      .then(profile => {
        if (profile) {
          setChosenFriend(profile);
          setCreateStep('pick_mode');
        }
      })
      .catch(() => {});
  }, [initialFriendId]);

  // ── LOAD RECEIVED (badge count au montage + auto-tab si défis en attente) ──
  useEffect(() => {
    if (!user?.id) return;
    getPendingChallenges(user.id)
      .then(list => {
        setPendingList(list);
        if (list.length > 0 && !initialFriendId) setTab('received');
      })
      .catch(() => setPendingList([]));
  }, [user?.id, initialFriendId]);

  // ── LOAD RECEIVED + défis envoyés sans réponse ──
  const reloadReceived = useCallback(() => {
    if (!user?.id) return;
    setLoadRecv(true);
    const now = Date.now();
    Promise.all([
      getPendingChallenges(user.id).catch(() => [] as Challenge[]),
      getMatchHistory(user.id).catch(() => [] as Challenge[]),
    ]).then(([recv, hist]) => {
      setPendingList(recv);
      setSentPending(hist.filter(c =>
        c.challenger_id === user.id && c.status === 'pending' && new Date(c.expires_at).getTime() > now));
    }).finally(() => setLoadRecv(false));
  }, [user?.id]);

  useEffect(() => {
    if (tab === 'received' && recvStep === 'list') reloadReceived();
  }, [tab, recvStep, reloadReceived]);

  // ── LOAD HISTORY (+ crédit des mises gagnées en tant que challenger) ──
  const loadHistory = useCallback(() => {
    if (!user?.id) return;
    setLoadHist(true);
    getMatchHistory(user.id)
      .then(list => {
        setHistList(list);
        const claimed = readClaimed();
        const newlyClaimed: string[] = [];
        list.forEach(c => {
          if (c.status !== 'completed' || c.challenger_id !== user.id || claimed.includes(c.id)) return;
          const { stake: s } = getChallengeMeta(c.questions);
          if (s > 0 && c.challenger_score > (c.challenged_score ?? 0)) {
            addXP(languageCode, s, true);
            newlyClaimed.push(c.id);
          }
        });
        if (newlyClaimed.length) {
          try { localStorage.setItem(STAKE_CLAIMED_KEY, JSON.stringify([...claimed, ...newlyClaimed].slice(-200))); } catch { /* ignore */ }
        }
      })
      .catch(() => setHistList([]))
      .finally(() => setLoadHist(false));
  }, [user?.id, languageCode]);

  useEffect(() => {
    if (tab === 'history') loadHistory();
  }, [tab, loadHistory]);

  // Résultats : besoin de l'historique pour le bilan face à l'adversaire
  useEffect(() => {
    if ((recvStep === 'results' || createStep === 'sent') && user?.id) {
      getMatchHistory(user.id).then(setHistList).catch(() => {});
    }
  }, [recvStep, createStep, user?.id]);

  // ── DÉCOMPTE avant quiz ──
  useEffect(() => {
    if (countdown === null) return;
    if (countdown === 0) {
      const id = setTimeout(() => { setCountdown(null); qStart.current = Date.now(); }, 700);
      return () => clearTimeout(id);
    }
    const id = setTimeout(() => setCountdown(c => c !== null ? c - 1 : null), 1000);
    return () => clearTimeout(id);
  }, [countdown]);

  // ── TIMER ──
  useEffect(() => {
    if (!timerActive) return;
    if (timeLeft <= 0) {
      if (createStep === 'playing') doMyAdvance.current?.(null);
      if (recvStep   === 'playing') doRvAdvance.current?.(null);
      return;
    }
    const id = setTimeout(() => setTimeLeft(s => s - 1), 1000);
    return () => clearTimeout(id);
  }, [timeLeft, timerActive, createStep, recvStep]);

  // ── Toast ──
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(id);
  }, [toast]);

  const shareText = useCallback(async (text: string) => {
    try {
      if (navigator.share) { await navigator.share({ text }); return; }
      await navigator.clipboard.writeText(text);
      setToast(t('challenge.arena.copied', 'Message copié'));
    } catch { /* partage annulé */ }
  }, [t]);

  // ── SAVE CHALLENGE ──
  const saveChallenge = useCallback(async (finalScore: number, answers: (string | null)[]) => {
    if (!user?.id || !chosenFriend) return;
    setCreateStep('saving');
    setSaveErr(null);
    const meta: ChallengeMeta = { theme: chosenTheme, format: chosenFormat, stake };
    const qs = myQs.map((q, i) => ({ ...q, challenger_answer: answers[i] ?? null, ...(i === 0 ? { meta } : {}) }));
    try {
      await createChallenge(user.id, chosenFriend.id, 'ba' /* valeur neutre requise par la DB */, 'cultural', qs, finalScore);
      const earned = challengeXP(finalScore, false, 0);
      addXP(languageCode, earned);
      setMyXPEarned(earned);
      setCreateStep('sent');
    } catch (e: any) {
      setSaveErr(e?.message || t('challenge.networkError'));
    }
  }, [user, chosenFriend, chosenTheme, chosenFormat, stake, myQs, t, languageCode]);

  // ── ADVANCE MY QUESTION ──
  const advanceMyQ = useCallback((choice: string | null) => {
    if (myLocked) return;
    const correct   = myQs[myIdx]?.correct;
    const isCorrect = choice !== null && choice === correct;
    const newScore  = isCorrect ? myScore + 1 : myScore;
    const answers   = [...myAnswers, choice];
    setMySelected(choice);
    setMyLocked(true);
    setMyAnswers(answers);
    setMyTimes(ts => [...ts, Date.now() - qStart.current]);
    if (isCorrect) setMyScore(newScore);
    setTimeout(() => {
      setMySelected(null);
      setMyLocked(false);
      setTimeLeft(mySeconds);
      qStart.current = Date.now();
      const next = myIdx + 1;
      if (next >= myQs.length) saveChallenge(newScore, answers);
      else setMyIdx(next);
    }, 900);
  }, [myLocked, myIdx, myQs, myScore, myAnswers, mySeconds, saveChallenge]);

  doMyAdvance.current = advanceMyQ;

  // ── FINISH RECEIVED ──
  const finishRecv = useCallback(async (finalScore: number) => {
    if (!activeChall) return;
    try { await completeChallenge(activeChall.id, finalScore); } catch (_) { /* ignore */ }
    const { stake: s } = getChallengeMeta(activeChall.questions);
    const won    = finalScore > activeChall.challenger_score;
    const earned = challengeXP(finalScore, won, s);
    addXP(languageCode, earned);
    setRvXPEarned(earned);
    setRvScore(finalScore);
    setPendingList(prev => prev.filter(c => c.id !== activeChall.id));
    setRecvStep('results');
  }, [activeChall, languageCode]);

  // ── ADVANCE RECEIVED QUESTION ──
  const advanceRvQ = useCallback((choice: string | null) => {
    if (!activeChall || rvLocked) return;
    const correct   = activeChall.questions[rvIdx]?.correct;
    const isCorrect = choice !== null && choice === correct;
    const newScore  = isCorrect ? rvScore + 1 : rvScore;
    setRvSelected(choice);
    setRvLocked(true);
    setRvAnswers(a => [...a, choice]);
    setRvTimes(ts => [...ts, Date.now() - qStart.current]);
    if (isCorrect) setRvScore(newScore);
    setTimeout(() => {
      setRvSelected(null);
      setRvLocked(false);
      setTimeLeft(rvSeconds);
      qStart.current = Date.now();
      const next = rvIdx + 1;
      if (next >= activeChall.questions.length) finishRecv(newScore);
      else setRvIdx(next);
    }, 1100);
  }, [rvLocked, rvIdx, rvScore, rvSeconds, activeChall, finishRecv]);

  doRvAdvance.current = advanceRvQ;

  // ── START MY GAME ──
  const startMyGame = useCallback(() => {
    const count = FORMAT_SPECS[chosenFormat].questions;
    const qs = shuffle(culturalPool(chosenTheme)).slice(0, count);
    setMyQs(qs); setMyIdx(0); setMyScore(0);
    setMySelected(null); setMyLocked(false);
    setMyAnswers([]); setMyTimes([]);
    setTimeLeft(FORMAT_SPECS[chosenFormat].seconds);
    setCountdown(3);
    setCreateStep('playing');
  }, [chosenFormat, chosenTheme]);

  // ── START RECEIVED GAME ──
  const startRecvGame = useCallback((challenge: Challenge) => {
    setActiveChall(challenge);
    setRvIdx(0); setRvScore(0);
    setRvSelected(null); setRvLocked(false);
    setRvAnswers([]); setRvTimes([]);
    setTimeLeft(FORMAT_SPECS[getChallengeMeta(challenge.questions).format].seconds);
    setCountdown(3);
    setRecvStep('playing');
  }, []);

  const handleDecline = useCallback(async (c: Challenge) => {
    setPendingList(prev => prev.filter(x => x.id !== c.id));
    try { await declineChallenge(c.id); } catch { reloadReceived(); }
  }, [reloadReceived]);

  const resetCreate = useCallback(() => {
    setCreateStep('pick_friend');
    setChosenFriend(null);
    setMyQs([]); setMyIdx(0); setMyScore(0);
    setMyAnswers([]); setMyTimes([]);
    setSaveErr(null);
  }, []);

  const leaveResults = useCallback(() => {
    setRecvStep('list');
    setActiveChall(null);
    setRvScore(0); setRvXPEarned(0);
  }, []);

  // ── Libellés ──
  const themeLabel  = (th: ChallengeTheme) => t(THEME_KEYS[th], th === 'mix' ? 'Mélange' : th);
  const formatLabel = (f: ChallengeFormat) => t(`challenge.arena.formats.${f}`, { eclair: 'Éclair', classique: 'Classique', marathon: 'Marathon' }[f]);
  const formatSpec  = (f: ChallengeFormat) => {
    const { questions, seconds } = FORMAT_SPECS[f];
    // Durée arrondie à la demi-minute (ex. 5 × 30 s → « 2,5 »)
    const min = (Math.round((questions * seconds) / 30) / 2).toLocaleString(i18n.language);
    return t('challenge.arena.formatSpec', { q: questions, min, defaultValue: '{{q}} Q · {{min}} MIN' });
  };
  const stakeLabel  = (s: number) => s > 0
    ? t('challenge.arena.stakeOf', { xp: s, defaultValue: 'mise {{xp}} XP' })
    : t('challenge.arena.noStake', 'sans mise');
  const metaLine    = (m: ChallengeMeta) => `${themeLabel(m.theme)} · ${formatLabel(m.format)} · ${stakeLabel(m.stake)}`;
  const firstName   = (name?: string | null) => (name || t('challenge.player')).split(' ')[0];

  const dayLabel = (iso: string) => {
    const d = new Date(iso);
    const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const days = Math.round((startOf(new Date()) - startOf(d)) / 86_400_000);
    if (days <= 0) return t('challenge.arena.today', "aujourd'hui");
    if (days === 1) return t('challenge.arena.yesterday', 'hier');
    return d.toLocaleDateString(i18n.language, { weekday: 'long' });
  };

  const hoursLeft = (iso: string) => Math.max(0, Math.floor((new Date(iso).getTime() - Date.now()) / 3_600_000));

  const avgSeconds = (times: number[]) => {
    if (!times.length) return '—';
    const s = times.reduce((a, b) => a + b, 0) / times.length / 1000;
    return `${s.toLocaleString(i18n.language, { maximumFractionDigits: 1, minimumFractionDigits: 1 })} s`;
  };

  // Bilan victoires – défaites face à un adversaire (7 derniers jours)
  const recordVs = (opponentId: string) => {
    let w = 0, l = 0;
    histList.forEach(c => {
      if (c.status !== 'completed') return;
      const iAmChallenger = c.challenger_id === user?.id;
      const other = iAmChallenger ? c.challenged_id : c.challenger_id;
      if (other !== opponentId) return;
      const mine   = iAmChallenger ? c.challenger_score : (c.challenged_score ?? 0);
      const theirs = iAmChallenger ? (c.challenged_score ?? 0) : c.challenger_score;
      if (mine > theirs) w++; else if (mine < theirs) l++;
    });
    return `${w} – ${l}`;
  };

  // ── NOT LOGGED IN ──
  if (!user?.id) {
    return (
      <div className="ar" data-theme={theme}>
        <Crenel />
        {renderTitleBar(t('challenge.arena.arena', 'Arène'), onBackToMenu)}
        <div className="ar-body">
          <div className="ar-niche">
            <div className="ar-kicker">{t('challenge.arena.modeLabel', 'MODE DÉFI')}</div>
            <div className="ar-niche__display">{t('challenge.arena.lamb', 'Làmb')}</div>
            <p className="ar-niche__sub">{t('challenge.loginRequired')}</p>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════
  // BLOCS COMMUNS
  // ═══════════════════════════════════════════════
  function renderTitleBar(title: string, onBack: () => void) {
    return (
      <div className="ar-titlebar ar-titlebar--stacked">
        <BackButton onClick={onBack} />
        <div className="ar-titlebar__text">
          <div className="ar-kicker">{t('challenge.arena.modeLabel', 'MODE DÉFI')}</div>
          <h1 className="ar-titlebar__title">{title}</h1>
        </div>
      </div>
    );
  }

  function renderTabs() {
    const tabs: { id: Tab; label: string }[] = [
      { id: 'create',   label: t('challenge.arena.tabs.create', 'Créer') },
      { id: 'received', label: t('challenge.arena.tabs.received', 'Reçus') },
      { id: 'history',  label: t('challenge.arena.tabs.history', 'Historique') },
    ];
    return (
      <div className="ar-tabs" role="tablist">
        {tabs.map(({ id, label }) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id}
            className={`ar-tabs__tab${tab === id ? ' is-active' : ''}`} onClick={() => setTab(id)}>
            {label}
            {id === 'received' && pendingList.length > 0 && (
              <span className="ar-badge">{pendingList.length > 9 ? '9+' : pendingList.length}</span>
            )}
          </button>
        ))}
      </div>
    );
  }


  function Streak({ n }: { n: number }) {
    return <span className="ar-streak"><span className="ar-diamond" aria-hidden="true" />{t('challenge.arena.streak', { n, defaultValue: 'série {{n}}' })}</span>;
  }



  // ═══════════════════════════════════════════════
  // DUEL EN COURS (6f)
  // ═══════════════════════════════════════════════
  function renderDuel(opts: {
    q: ChallengeQuestion; idx: number; total: number; seconds: number;
    myS: number; oppName: string; oppScore: number | null;
    selected: string | null; locked: boolean; onAnswer: (c: string) => void;
    themeName: string;
  }) {
    const { q, idx, total, seconds, myS, oppName, oppScore, selected, locked, onAnswer, themeName } = opts;
    const pct       = Math.max(0, Math.min(100, (timeLeft / seconds) * 100));
    const urgent    = timeLeft <= 3;
    const oppAnswer = q.challenger_answer;
    const qText     = quizQuestionText(q.word, i18n.language);
    const longQ     = qText.length > 40;

    const duel = (
      <>
        <div className="ar-duel">
          <div className="ar-duel__side">
            <div className="ar-duel__who">
              <Avatar name={user?.name} size={34} ring />
              <div>
                <div className="ar-duel__name">{myName}</div>
                <div className="ar-duel__score">{myS}</div>
              </div>
            </div>
            <div className="ar-duel__bar"><span className="is-me" style={{ width: `${(myS / total) * 100}%` }} /></div>
          </div>
          <div className={`ar-timer${urgent ? ' is-urgent' : ''}`} style={{ '--pct': `${pct}%` } as React.CSSProperties}
            role="timer" aria-label={t('challenge.arena.secondsLeft', { s: timeLeft, defaultValue: '{{s}} secondes' }) as string}>
            <span>{timeLeft}</span>
          </div>
          <div className="ar-duel__side ar-duel__side--opp">
            <div className="ar-duel__who">
              <Avatar name={oppName} size={34} />
              <div>
                <div className="ar-duel__name">{firstName(oppName)}</div>
                <div className="ar-duel__score">{oppScore ?? '?'}</div>
              </div>
            </div>
            <div className="ar-duel__bar ar-duel__bar--rtl"><span style={{ width: `${((oppScore ?? 0) / total) * 100}%` }} /></div>
          </div>
        </div>

        <div className="ar-body">
          <div className="ar-kicker ar-kicker--muted ar-center">
            {t('challenge.questionCounter', { current: idx + 1, total }).toUpperCase()} · {themeName.toUpperCase()}
          </div>
          <div className="ar-niche ar-niche--question">
            <div className="ar-kicker">{t('challenge.arena.culturalQuiz', 'QUIZ CULTUREL')}</div>
            <p className={`ar-question${longQ ? ' is-long' : ''}`}>{qText}</p>
          </div>
          <div className="ar-options">
            {q.choices.map((choice, i) => {
              const isSel = selected === choice;
              const isOk  = choice === q.correct;
              let state = '';
              if (locked) state = isOk ? ' is-correct' : isSel ? ' is-wrong' : ' is-muted';
              return (
                <button key={choice} type="button" className={`ar-option${state}`}
                  onClick={() => !locked && onAnswer(choice)} disabled={locked}>
                  <span className="ar-option__mark" aria-hidden="true">
                    {locked && isOk ? <Check size={15} strokeWidth={2.6} />
                      : locked && isSel ? <X size={15} strokeWidth={2.6} />
                      : String.fromCharCode(65 + i)}
                  </span>
                  <span className="ar-option__text">{quizOptionText(q.word, choice, i18n.language)}</span>
                  {locked && oppAnswer === choice && (
                    <span className="ar-option__opp">
                      <Avatar name={oppName} size={20} />
                      {firstName(oppName).toUpperCase()}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </>
    );

    if (countdown === null) return duel;
    const isGo = countdown === 0;
    return (
      <div className="ar-countdown-wrap">
        <div className="ar-countdown-bg">{duel}</div>
        <div className="ar-countdown">
          <div className={`ar-countdown__num${isGo ? ' is-go' : ''}`}>{isGo ? t('challenge.go') : countdown}</div>
          {!isGo && <div className="ar-kicker ar-kicker--muted">{t('challenge.getReady').toUpperCase()}</div>}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════
  // RÉSULTAT (6g) — reçu terminé ou défi envoyé
  // ═══════════════════════════════════════════════
  function renderResult(opts: {
    kicker: string; wolof?: string; title: string; sub?: string;
    mine: number; theirs: number | null; oppName: string; oppId: string;
    answers: (string | null)[]; questions: ChallengeQuestion[]; times: number[];
    xp: number; primary: { label: string; onClick: () => void };
    secondary: { label: string; onClick: () => void }; shareMsg: string;
  }) {
    const { kicker, wolof, title, sub, mine, theirs, oppName, oppId, answers, questions, times, xp, primary, secondary, shareMsg } = opts;
    const cells = questions.map((q, i) => (answers[i] !== undefined && answers[i] === q.correct ? 'win' : 'loss') as 'win' | 'loss');
    return (
      <div className="ar" data-theme={theme}>
        <Crenel />
        <div className="ar-body ar-body--result">
          <div className="ar-result__head">
            <div className="ar-kicker">{kicker}</div>
            {wolof && <div className="ar-result__wolof">{wolof}</div>}
            <h1 className="ar-result__title">{title}</h1>
            {sub && <p className="ar-niche__sub">{sub}</p>}
          </div>

          <div className="ar-niche ar-niche--wide">
            <div className="ar-result__scores">
              <div className="ar-result__player">
                <Avatar name={user?.name} size={66} ring />
                <div className="ar-result__score">{mine}</div>
                <div className="ar-result__label">{myName}</div>
              </div>
              <div className="ar-result__sep">—</div>
              <div className="ar-result__player is-opp">
                <Avatar name={oppName} size={52} />
                <div className="ar-result__score ar-result__score--sm">{theirs ?? '?'}</div>
                <div className="ar-result__label">{firstName(oppName)}</div>
              </div>
            </div>
            <Frieze items={cells} />
            <div className="ar-kicker ar-kicker--muted ar-center ar-mt6">
              {t('challenge.arena.yourAnswers', { n: questions.length, defaultValue: 'TES {{n}} RÉPONSES' })}
            </div>
          </div>

          <StatTiles tiles={[
            { value: `+${xp}`, label: t('challenge.arena.xpEarned', 'XP GAGNÉS') },
            { value: recordVs(oppId), label: t('challenge.arena.vs', { name: firstName(oppName).toUpperCase(), defaultValue: 'FACE À {{name}}' }) },
            { value: avgSeconds(times), label: t('challenge.arena.perAnswer', 'PAR RÉPONSE') },
          ]} />
        </div>

        <div className="ar-footer ar-footer--row">
          <button type="button" className="ar-btn ar-btn--ghost" onClick={() => shareText(shareMsg)}>
            {t('challenge.arena.share', 'Partager')}
          </button>
          <button type="button" className="ar-btn ar-btn--primary" onClick={primary.onClick}>{primary.label}</button>
        </div>
        <button type="button" className="ar-link" onClick={secondary.onClick}>{secondary.label}</button>
        {toast && <div className="ar-toast" role="status">{toast}</div>}
      </div>
    );
  }

  // ═══════════════════════════════════════════════
  // ONGLET CRÉER
  // ═══════════════════════════════════════════════
  function renderPickFriend() {
    const selected = chosenFriend;
    return (
      <>
        <div className="ar-body">
          {renderTabs()}

          <div className="ar-niche ar-niche--wide">
            <div className="ar-kicker">{t('challenge.arena.arenaLamb', 'ARÈNE · LÀMB')}</div>
            <div className="ar-niche__display">{t('challenge.arena.lamb', 'Làmb')}</div>
            <div className="ar-niche__sub">{t('challenge.arena.lambSub', "L'arène de lutte")}</div>
            <div className="ar-faceoff">
              <div className="ar-faceoff__player">
                <span className="ar-avatar ar-avatar--xl ar-avatar--ring">{myInitial}</span>
                <div className="ar-faceoff__name">{myName}</div>
              </div>
              <div className="ar-vs"><span>VS</span></div>
              <div className="ar-faceoff__player">
                {selected
                  ? <span className="ar-avatar ar-avatar--xl">{initial(selected.display_name || selected.username)}</span>
                  : <span className="ar-avatar ar-avatar--xl ar-avatar--empty">?</span>}
                <div className={`ar-faceoff__name${selected ? '' : ' is-muted'}`}>
                  {selected ? firstName(selected.display_name || selected.username) : t('challenge.arena.opponent', 'Adversaire')}
                </div>
              </div>
            </div>
          </div>

          {onOpenMemoryDuel && (
            <button type="button" className="ar-card" onClick={onOpenMemoryDuel}
              style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit', boxSizing: 'border-box' }}>
              <span style={{ width: 46, height: 46, borderRadius: '50%', background: '#5E6B3A', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFF8EC" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M8 3H19V16H8Z M5 7V20H16" /></svg>
              </span>
              <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <span style={{ fontWeight: 700, fontSize: 16 }}>{t('memoryDuel.title', 'Memory Duel')}</span>
                <span style={{ fontSize: 13, opacity: 0.75 }}>{t('challenge.arena.memoryDuelSub', 'Retrouve les paires de mots avant ton ami')}</span>
              </span>
              <ChevronRight size={20} strokeWidth={2} aria-hidden="true" />
            </button>
          )}

          <div className="ar-section-head">
            <h2 className="ar-h2">{t('challenge.arena.chooseOpponent', 'Choisis ton adversaire')}</h2>
            <button type="button" className={`ar-btn-sm${showAddFriend ? ' is-active' : ''}`}
              onClick={() => { setShowAddFriend(v => !v); setAddQuery(''); setAddResults([]); setAddMsg(null); }}>
              {showAddFriend ? t('challenge.arena.close', 'Fermer') : t('challenge.arena.add', '+ Ajouter')}
            </button>
          </div>

          {showAddFriend && (
            <div className="ar-card ar-search">
              <input
                type="text"
                value={addQuery}
                onChange={e => handleAddSearch(e.target.value)}
                placeholder={t('challenge.usernamePlaceholder') as string}
                className="ar-input"
                aria-label={t('challenge.searchUser') as string}
                autoFocus
              />
              {addLoading && <p className="ar-hint">{t('challenge.searching')}</p>}
              {addMsg && <p className={addMsg.ok ? 'ar-hint is-ok' : 'ar-hint is-err'}>{addMsg.text}</p>}
              {addResults.map(r => (
                <div key={r.id} className="ar-search__row">
                  <Avatar name={r.display_name || r.username} size={36} />
                  <div className="ar-grow">
                    <div className="ar-row__name ar-row__name--sm">{r.display_name || r.username || t('challenge.player')}</div>
                    <div className="ar-row__meta">{displayXP(r)} XP</div>
                  </div>
                  <button type="button" className="ar-btn-sm" onClick={() => handleFollowFromSearch(r)}>{t('challenge.follow')}</button>
                </div>
              ))}
              {!addLoading && addQuery.trim().length >= 2 && addResults.length === 0 && !addMsg && (
                <p className="ar-hint ar-center">{t('challenge.noResults')}</p>
              )}
            </div>
          )}

          {loadFriends && <p className="ar-hint">{t('challenge.loading')}</p>}
          {!loadFriends && friends.length === 0 && !showAddFriend && (
            <div className="ar-empty">
              <p>{t('challenge.noFriendsYet')}</p>
              <p className="ar-hint">{t('challenge.arena.useAdd', 'Utilise « + Ajouter » pour trouver des amis.')}</p>
            </div>
          )}

          <div className="ar-list" role="radiogroup">
            {friends.map(f => {
              const isSel = selected?.id === f.id;
              return (
                <button key={f.id} type="button" role="radio" aria-checked={isSel}
                  className={`ar-row ar-row--friend${isSel ? ' is-selected' : ''}`}
                  onClick={() => setChosenFriend(isSel ? null : f)}>
                  <Avatar name={f.display_name || f.username} />
                  <div className="ar-grow">
                    <div className="ar-row__name">{f.display_name || f.username || t('challenge.player')}</div>
                    <div className="ar-row__meta">{displayXP(f)} XP <span>·</span> <Streak n={f.daily_streak} /></div>
                  </div>
                  {isSel
                    ? <span className="ar-radio is-on" aria-hidden="true"><span /></span>
                    : <ChevronRight size={16} className="ar-chevron" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        </div>

        <div className="ar-footer">
          <button type="button" className="ar-btn ar-btn--primary" disabled={!selected}
            onClick={() => setCreateStep('pick_mode')}>
            {selected
              ? <>{t('challenge.arena.challengeX', { name: firstName(selected.display_name || selected.username), defaultValue: 'Défier {{name}}' })}
                  <span className="ar-btn__soft">&nbsp;· {t('challenge.arena.nQuestions', { n: FORMAT_SPECS[chosenFormat].questions, defaultValue: '{{n}} questions' })}</span></>
              : t('challenge.arena.pickFirst', 'Choisis un adversaire')}
          </button>
        </div>
      </>
    );
  }

  function renderPickMode() {
    const f = chosenFriend;
    return (
      <>
        <div className="ar-body">
          <div className="ar-row">
            <Avatar name={f?.display_name || f?.username} />
            <div className="ar-grow">
              <div className="ar-kicker ar-kicker--sm">{t('challenge.arena.opponentCaps', 'ADVERSAIRE')}</div>
              <div className="ar-row__name">{f?.display_name || f?.username}</div>
            </div>
            <button type="button" className="ar-textbtn" onClick={() => setCreateStep('pick_friend')}>
              {t('challenge.arena.change', 'Changer')}
            </button>
          </div>

          <div className="ar-kicker ar-kicker--muted">{t('challenge.arena.theme', 'THÈME')}</div>
          <div className="ar-chips" role="radiogroup">
            {AVAILABLE_THEMES.map(th => (
              <button key={th} type="button" role="radio" aria-checked={chosenTheme === th}
                className={`ar-chip${chosenTheme === th ? ' is-active' : ''}`} onClick={() => setChosenTheme(th)}>
                {themeLabel(th)}
              </button>
            ))}
          </div>

          <div className="ar-kicker ar-kicker--muted">{t('challenge.arena.format', 'FORMAT')}</div>
          <div className="ar-formats" role="radiogroup">
            {FORMAT_ORDER.map(fm => (
              <button key={fm} type="button" role="radio" aria-checked={chosenFormat === fm}
                className={`ar-format${chosenFormat === fm ? ' is-active' : ''}`} onClick={() => setChosenFormat(fm)}>
                <span className="ar-format__name">{formatLabel(fm)}</span>
                <span className="ar-format__spec">{formatSpec(fm)}</span>
              </button>
            ))}
          </div>

          <div className="ar-kicker ar-kicker--muted">{t('challenge.arena.stake', 'MISE')}</div>
          <div className="ar-card ar-stake">
            <div className="ar-grow">
              <div className="ar-stake__value">{stake > 0 ? `${stake} XP` : t('challenge.arena.noStakeCap', 'Sans mise')}</div>
              <div className="ar-row__meta">{t('challenge.arena.stakeHint', 'Le gagnant remporte la mise')}</div>
            </div>
            <div className="ar-stake__btns">
              <button type="button" className="ar-square" disabled={stake <= 0}
                onClick={() => setStake(s => Math.max(0, s - STAKE_STEP))} aria-label="−">−</button>
              <button type="button" className="ar-square" disabled={stake >= STAKE_MAX}
                onClick={() => setStake(s => Math.min(STAKE_MAX, s + STAKE_STEP))} aria-label="+">+</button>
            </div>
          </div>
        </div>

        <div className="ar-footer">
          <button type="button" className="ar-btn ar-btn--primary" onClick={startMyGame}>
            {t('challenge.arena.launch', 'Lancer le défi')}
          </button>
          <p className="ar-hint ar-center ar-mt6">{t('challenge.arena.launchHint', 'Tu joues ta manche, puis le défi est envoyé.')}</p>
        </div>
      </>
    );
  }

  // ═══════════════════════════════════════════════
  // ONGLET REÇUS (6e)
  // ═══════════════════════════════════════════════
  function renderReceivedList() {
    return (
      <div className="ar-body">
        {renderTabs()}

        {loadRecv && pendingList.length === 0 && <p className="ar-hint">{t('challenge.loading')}</p>}
        {!loadRecv && pendingList.length === 0 && (
          <div className="ar-empty"><p>{t('challenge.noPendingChallenges')}</p></div>
        )}

        {pendingList.map(c => {
          const meta   = getChallengeMeta(c.questions);
          const h      = hoursLeft(c.expires_at);
          const urgent = h < URGENT_HOURS;
          return (
            <div key={c.id} className={`ar-card ar-invite${urgent ? ' is-urgent' : ''}`}>
              <div className="ar-invite__top">
                <Avatar name={c.challenger_username} size={42} />
                <div className="ar-grow">
                  <div className="ar-row__name ar-row__name--md">
                    {t('challenge.arena.challengesYou', { name: firstName(c.challenger_username), defaultValue: '{{name}} te défie' })}
                  </div>
                  <div className="ar-row__meta">{metaLine(meta)}</div>
                </div>
                <span className={`ar-timeleft${urgent ? ' is-urgent' : ''}`}>{h < 1 ? '<1H' : `${h}H`}</span>
              </div>
              <div className="ar-invite__actions">
                <button type="button" className="ar-btn ar-btn--quiet" onClick={() => handleDecline(c)}>
                  {t('challenge.arena.decline', 'Refuser')}
                </button>
                <button type="button" className="ar-btn ar-btn--primary ar-btn--md" onClick={() => startRecvGame(c)}>
                  {t('challenge.arena.accept', 'Relever le défi')}
                </button>
              </div>
            </div>
          );
        })}

        {sentPending.length > 0 && (
          <>
            <div className="ar-divider">
              <span className="ar-diamond" /><span className="ar-kicker ar-kicker--muted">{t('challenge.arena.awaiting', 'EN ATTENTE DE RÉPONSE')}</span><span className="ar-diamond" />
            </div>
            {sentPending.map(c => (
              <div key={c.id} className="ar-row ar-row--dashed">
                <Avatar name={c.challenged_username} size={36} />
                <div className="ar-grow ar-row__text">
                  {t('challenge.arena.notPlayedYet', { name: firstName(c.challenged_username), defaultValue: "{{name}} n'a pas encore joué ton défi" })}
                </div>
                <button type="button" className="ar-textbtn"
                  onClick={() => shareText(t('challenge.arena.nudgeMsg', { name: firstName(c.challenged_username), defaultValue: '{{name}}, ton défi Jogolinga t’attend dans l’Arène !' }))}>
                  {t('challenge.arena.nudge', 'Relancer')}
                </button>
              </div>
            ))}
          </>
        )}
        {toast && <div className="ar-toast" role="status">{toast}</div>}
      </div>
    );
  }

  // ═══════════════════════════════════════════════
  // ONGLET HISTORIQUE (6h)
  // ═══════════════════════════════════════════════
  function renderHistory() {
    const done = histList.filter(c => c.status === 'completed');
    const outcome = (c: Challenge) => {
      const iAmChallenger = c.challenger_id === user.id;
      const mine   = iAmChallenger ? c.challenger_score : (c.challenged_score ?? 0);
      const theirs = iAmChallenger ? (c.challenged_score ?? 0) : c.challenger_score;
      return { mine, theirs, res: (mine > theirs ? 'win' : mine < theirs ? 'loss' : 'neutral') as 'win' | 'loss' | 'neutral' };
    };
    const wins   = done.filter(c => outcome(c).res === 'win').length;
    const losses = done.filter(c => outcome(c).res === 'loss').length;
    const rate   = done.length ? Math.round((wins / done.length) * 100) : 0;
    const rows   = histList.filter(c => c.status !== 'pending');

    return (
      <div className="ar-body">
        {renderTabs()}

        {loadHist && histList.length === 0 && <p className="ar-hint">{t('challenge.loading')}</p>}
        {!loadHist && rows.length === 0 && (
          <div className="ar-empty"><p>{t('challenge.noGamesThisWeek')}</p></div>
        )}

        {rows.length > 0 && (
          <>
            <StatTiles tiles={[
              { value: String(wins),   label: t('challenge.arena.wins', 'VICTOIRES') },
              { value: String(losses), label: t('challenge.arena.losses', 'DÉFAITES') },
              { value: `${rate} %`,    label: t('challenge.arena.winRate', 'RÉUSSITE') },
            ]} />
            {done.length > 0 && (
              <div>
                <Frieze tall items={done.slice(0, 10).reverse().map(c => outcome(c).res)} />
                <div className="ar-kicker ar-kicker--muted ar-mt6">
                  {t('challenge.arena.lastN', { n: Math.min(10, done.length), defaultValue: '{{n}} DERNIERS DÉFIS' })}
                </div>
              </div>
            )}
            <div className="ar-kicker ar-kicker--muted">{t('challenge.arena.thisWeek', 'CETTE SEMAINE')}</div>
            <div className="ar-list">
              {rows.map(c => {
                const iAmChallenger = c.challenger_id === user.id;
                const oppName = iAmChallenger ? c.challenged_username : c.challenger_username;
                const meta    = getChallengeMeta(c.questions);
                const declined = c.status === 'declined';
                const { mine, theirs, res } = outcome(c);
                return (
                  <div key={c.id} className="ar-row ar-row--hist">
                    <Avatar name={oppName} size={38} />
                    <div className="ar-grow">
                      <div className="ar-row__name ar-row__name--sm">{oppName || t('challenge.player')}</div>
                      <div className="ar-row__meta">{themeLabel(meta.theme)} · {dayLabel(c.created_at)}</div>
                    </div>
                    <div className="ar-hist__score">
                      <div className="ar-hist__nums">{declined ? '—' : `${mine} – ${theirs}`}</div>
                      <div className={`ar-hist__res is-${declined ? 'neutral' : res}`}>
                        {declined ? t('challenge.arena.declined', 'REFUSÉ')
                          : res === 'win' ? t('challenge.arena.win', 'VICTOIRE')
                          : res === 'loss' ? t('challenge.arena.loss', 'DÉFAITE')
                          : t('challenge.arena.tie', 'ÉGALITÉ')}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════
  // RENDU PRINCIPAL
  // ═══════════════════════════════════════════════

  // Duel (création)
  if (tab === 'create' && createStep === 'playing') {
    const q = myQs[myIdx];
    if (!q) return null;
    const oppName = chosenFriend?.display_name || chosenFriend?.username || t('challenge.player');
    return (
      <div className="ar" data-theme={theme}>
        <Crenel />
        {renderDuel({
          q, idx: myIdx, total: myQs.length, seconds: mySeconds, myS: myScore,
          oppName, oppScore: null, selected: mySelected, locked: myLocked,
          onAnswer: advanceMyQ, themeName: themeLabel(chosenTheme),
        })}
      </div>
    );
  }

  // Duel (défi reçu)
  if (tab === 'received' && recvStep === 'playing' && activeChall) {
    const q = activeChall.questions[rvIdx];
    if (!q) return null;
    const meta = getChallengeMeta(activeChall.questions);
    const hasOppAnswers = activeChall.questions.some(x => x.challenger_answer !== undefined);
    const upTo = rvLocked ? rvIdx + 1 : rvIdx;
    const oppScore = hasOppAnswers
      ? activeChall.questions.slice(0, upTo).filter(x => x.challenger_answer === x.correct).length
      : null;
    return (
      <div className="ar" data-theme={theme}>
        <Crenel />
        {renderDuel({
          q, idx: rvIdx, total: activeChall.questions.length, seconds: rvSeconds, myS: rvScore,
          oppName: activeChall.challenger_username || t('challenge.player'), oppScore,
          selected: rvSelected, locked: rvLocked, onAnswer: advanceRvQ, themeName: themeLabel(meta.theme),
        })}
      </div>
    );
  }

  // Résultat d'un défi reçu
  if (tab === 'received' && recvStep === 'results' && activeChall) {
    const meta   = getChallengeMeta(activeChall.questions);
    const theirs = activeChall.challenger_score;
    const won    = rvScore > theirs;
    const tied   = rvScore === theirs;
    const opp    = activeChall.challenger_username || t('challenge.player');
    return renderResult({
      kicker: `${t('challenge.arena.finished', 'DÉFI TERMINÉ')} · ${themeLabel(meta.theme).toUpperCase()}`,
      wolof: won ? 'Yow mooy gaynde bi !' : undefined,
      title: won ? t('challenge.arena.youWin', "Tu remportes l'arène")
        : tied ? t('challenge.arena.youTie', "Égalité dans l'arène")
        : t('challenge.arena.youLose', { name: firstName(opp), defaultValue: '{{name}} remporte l’arène' }),
      mine: rvScore, theirs, oppName: opp, oppId: activeChall.challenger_id,
      answers: rvAnswers, questions: activeChall.questions, times: rvTimes, xp: rvXPEarned,
      shareMsg: t('challenge.arena.shareResult', { mine: rvScore, theirs, name: firstName(opp), defaultValue: 'Arène Jogolinga : {{mine}} – {{theirs}} face à {{name}} !' }),
      primary: {
        label: t('challenge.arena.rematch', 'Revanche'),
        onClick: () => {
          const revProfile: UserProfile = {
            id: activeChall.challenger_id,
            username: activeChall.challenger_username || null,
            display_name: activeChall.challenger_username || null,
            avatar_url: null, total_xp: 0, daily_streak: 0,
            last_active: '', is_public: true,
          };
          setChosenTheme(AVAILABLE_THEMES.includes(meta.theme) ? meta.theme : 'mix');
          setChosenFormat(meta.format);
          setStake(meta.stake);
          setChosenFriend(revProfile);
          setCreateStep('pick_mode');
          leaveResults();
          setTab('create');
        },
      },
      secondary: { label: t('challenge.seeOtherChallenges'), onClick: leaveResults },
    });
  }

  // Défi envoyé
  if (tab === 'create' && (createStep === 'sent' || createStep === 'saving')) {
    const opp = chosenFriend?.display_name || chosenFriend?.username || t('challenge.player');
    if (createStep === 'saving') {
      return (
        <div className="ar" data-theme={theme}>
          <Crenel />
          <div className="ar-body ar-body--result">
            <div className="ar-empty">
              <p>{t('challenge.sendingChallenge')}</p>
              {saveErr && (
                <>
                  <p className="ar-hint is-err">{saveErr}</p>
                  <button type="button" className="ar-btn ar-btn--primary" onClick={() => saveChallenge(myScore, myAnswers)}>
                    {t('challenge.arena.retry', 'Réessayer')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      );
    }
    return renderResult({
      kicker: `${t('challenge.arena.sent', 'DÉFI ENVOYÉ')} · ${themeLabel(chosenTheme).toUpperCase()}`,
      title: t('challenge.arena.sentTitle', 'Défi lancé !'),
      sub: t('challenge.arena.sentSub', { name: firstName(opp), defaultValue: '{{name}} a 48 h pour te répondre.' }),
      mine: myScore, theirs: null, oppName: opp, oppId: chosenFriend?.id || '',
      answers: myAnswers, questions: myQs, times: myTimes, xp: myXPEarned,
      shareMsg: t('challenge.arena.shareSent', { score: myScore, total: myQs.length, name: firstName(opp), defaultValue: 'J’ai fait {{score}}/{{total}} dans l’Arène Jogolinga. À toi, {{name}} !' }),
      primary: { label: t('challenge.arena.newChallenge', 'Nouveau défi'), onClick: resetCreate },
      secondary: { label: t('challenge.backToMenu'), onClick: onBackToMenu },
    });
  }

  // Écrans à onglets
  const isConfig = tab === 'create' && createStep === 'pick_mode';
  return (
    <div className="ar" data-theme={theme}>
      <Crenel />
      {renderTitleBar(
        isConfig ? t('challenge.arena.newChallengeTitle', 'Nouveau défi') : t('challenge.arena.arena', 'Arène'),
        isConfig ? () => setCreateStep('pick_friend') : onBackToMenu,
      )}
      {tab === 'create' && createStep === 'pick_friend' && renderPickFriend()}
      {isConfig && renderPickMode()}
      {tab === 'received' && renderReceivedList()}
      {tab === 'history'  && renderHistory()}
    </div>
  );
};

function Avatar({ name, size = 46, ring = false }: { name?: string | null; size?: number; ring?: boolean }) {
  return (
    <span className={`ar-avatar${ring ? ' ar-avatar--ring' : ''}`} style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }} aria-hidden="true">
      {initial(name)}
    </span>
  );
}

function Frieze({ items, tall = false }: { items: ('win' | 'loss' | 'neutral')[]; tall?: boolean }) {
  return (
    <div className={`ar-frieze${tall ? ' ar-frieze--tall' : ''}`} aria-hidden="true">
      {items.map((s, i) => <span key={i} className={`ar-frieze__cell is-${s}`} />)}
    </div>
  );
}

function StatTiles({ tiles }: { tiles: { value: string; label: string }[] }) {
  return (
    <div className="ar-tiles">
      {tiles.map((tile, i) => (
        <div key={i} className="ar-tile">
          <div className="ar-tile__value">{tile.value}</div>
          <div className="ar-tile__label">{tile.label}</div>
        </div>
      ))}
    </div>
  );
}

// Créneaux ocre sous l'en-tête de l'app
const Crenel: React.FC = () => (
  <div className="ar-crenel" aria-hidden="true">
    {Array.from({ length: 8 }, (_, i) => <span key={i} />)}
  </div>
);

export default ChallengeMode;
